import { useEffect, useMemo, useState } from "react";
import { Input, Modal } from "@iq/models";
import { getProblems, linkChangeToProblem } from "../../services/ticket_service";
import "./LinkProblemModal.css";

function formatTicketLabel(problem) {
    const number = problem?.number || `PRB-${problem?.id ?? "?"}`;
    const title = problem?.title || "Untitled Problem";
    return `${number} - ${title}`;
}

export default function LinkProblemModal({
    isOpen,
    onClose,
    change,
    onCompleted,
}) {
    const [selectedProblemId, setSelectedProblemId] = useState("");
    const [loadingProblems, setLoadingProblems] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [problemSearch, setProblemSearch] = useState("");
    const [problems, setProblems] = useState([]);

    const filteredProblems = useMemo(() => {
        const search = problemSearch.trim().toLowerCase();

        return (Array.isArray(problems) ? problems : [])
            .filter((problem) => {
                if (!search) {
                    return true;
                }

                return [problem?.number, problem?.title, problem?.description]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(search));
            })
            .sort((left, right) => new Date(right?.created_at || 0) - new Date(left?.created_at || 0));
    }, [problemSearch, problems]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelectedProblemId("");
        setProblemSearch("");
        setError("");
        setSaving(false);
        setLoadingProblems(true);

        let cancelled = false;

        (async () => {
            try {
                const loadedProblems = await getProblems({ limit: 200 });
                if (!cancelled) {
                    setProblems(Array.isArray(loadedProblems) ? loadedProblems : []);
                }
            } catch (problemError) {
                if (!cancelled) {
                    setProblems([]);
                    setError(problemError?.message || "Failed to load problems");
                }
            } finally {
                if (!cancelled) {
                    setLoadingProblems(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    const selectedProblem = useMemo(
        () => filteredProblems.find((problem) => String(problem?.id) === String(selectedProblemId)),
        [filteredProblems, selectedProblemId]
    );

    async function handleSubmit(event) {
        event.preventDefault();

        if (!change?.id || !selectedProblemId) {
            return;
        }

        setSaving(true);
        setError("");

        try {
            await linkChangeToProblem(change.id, Number(selectedProblemId));
            onCompleted?.({ changeId: change.id, problemId: Number(selectedProblemId), problem: selectedProblem || null });
            onClose?.();
        } catch (submitError) {
            setError(submitError?.message || "Failed to link problem");
        } finally {
            setSaving(false);
        }
    }

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Link Problem"
            actionButtons={[
                {
                    key: "cancel-link-problem",
                    text: "Cancel",
                    color: "light",
                    onClick: onClose,
                    disabled: saving,
                },
                {
                    key: "submit-link-problem",
                    text: saving ? "Linking..." : "Link Problem",
                    color: "accent",
                    type: "submit",
                    form: "link-problem-form",
                    disabled: saving || !selectedProblemId,
                },
            ]}
            errorMessage={error}
            tall
        >
            <form id="link-problem-form" onSubmit={handleSubmit} className="link-problem-modal-form">
                <div>
                    <label htmlFor="link-problem-search">Search Problems</label>
                    <Input
                        id="link-problem-search"
                        type="search"
                        name="link-problem-search"
                        value={problemSearch}
                        onChange={(event) => setProblemSearch(event.target.value)}
                        placeholder="Search by problem number, title, or description"
                        disabled={loadingProblems}
                        colorScheme="light"
                    />
                </div>

                <div className="link-problem-panel-title-row">
                    <h3>Problems</h3>
                    <span>{filteredProblems.length} found</span>
                </div>

                <div className="link-problem-list" role="listbox" aria-label="Problems">
                    {loadingProblems ? (
                        <div className="modal-empty-state">Loading problems...</div>
                    ) : filteredProblems.length === 0 ? (
                        <div className="modal-empty-state">No problems found.</div>
                    ) : (
                        filteredProblems.map((problemItem) => {
                            const isSelected = String(selectedProblemId) === String(problemItem?.id);

                            return (
                                <button
                                    key={problemItem.id}
                                    type="button"
                                    className={[
                                        "link-problem-list-item",
                                        isSelected ? "link-problem-list-item--selected" : "",
                                    ].filter(Boolean).join(" ")}
                                    onClick={() => {
                                        setSelectedProblemId(String(problemItem.id));
                                        setError("");
                                    }}
                                >
                                    <div className="link-problem-list-item__headline">{problemItem?.title}</div>
                                    <div className="link-problem-list-item__meta">{problemItem.number}</div>
                                    <div className="link-problem-list-item__meta">
                                        <span>Status: {problemItem?.status || "-"}</span>
                                        <span>Priority: {problemItem?.priority || "-"}</span>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {selectedProblem ? (
                    <div className="link-problem-selected-summary">
                        Selected: {formatTicketLabel(selectedProblem)}
                    </div>
                ) : null}
            </form>
        </Modal>
    );
}
